/* istanbul ignore file */

import type { MouseEvent, UIEvent } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useWindowSize } from 'react-use';
import { toSentenceCase } from '@noths/polaris-client-utils';

import * as styles from './styles';

const FADE_MIN_THRESHOLD_IN_PX = 10;

export interface BreadcrumbLink {
  href: string;
  text: string;
}

export interface BreadcrumbLinkClickPayload {
  linkDepth: number;
}

export interface BreadcrumbsProps {
  links: BreadcrumbLink[];
  onLinkClick?: (
    e: MouseEvent<HTMLAnchorElement>,
    { linkDepth }: BreadcrumbLinkClickPayload,
  ) => void;
}

export const Breadcrumbs = ({ links, onLinkClick }: BreadcrumbsProps) => {
  const { width } = useWindowSize();
  const containerRef = useRef<HTMLOListElement | null>(null);
  const [shouldShowLeftFade, setShouldShowLeftFade] = useState(false);
  const getLinkDepth = (index: number) => links.length - index;

  const handleOnScroll = ({ target }: UIEvent<HTMLOListElement>) =>
    setShouldShowLeftFade((target as HTMLOListElement).scrollLeft >= FADE_MIN_THRESHOLD_IN_PX);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollLeft = containerRef.current.scrollWidth;
    }
  }, [containerRef, width]);

  const onClick = (e: MouseEvent<HTMLAnchorElement>, index: number) => {
    onLinkClick?.(e, { linkDepth: getLinkDepth(index) });
  };

  return (
    <nav
      aria-label="Breadcrumb"
      css={styles.breadcrumbNav}
      data-left-fade-visible={shouldShowLeftFade}
    >
      <ol css={styles.breadcrumbList} onScroll={handleOnScroll} ref={containerRef}>
        {links.map((link, index) => (
          <li css={styles.breadcrumbListItem} key={link.text}>
            <a css={styles.breadcrumbLink} href={link.href} onClick={(e) => onClick(e, index)}>
              {toSentenceCase(link.text.toLowerCase())}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
};
